import { EUserType } from "@dreamcam/messenger-sdk";
import { ResponseError } from "@dreamcam/web-sdk";
import { AppView } from "../babylon/AppView";
import { WebXRState } from "@babylonjs/core/XR/webXRTypes.js";
import { Env } from "../settings/Env";
import { MessageType } from "../interfaces/NotificationCenter";
import { text } from "../TextResources";
import { throttle, waitFor } from "../utils/AsyncUtils";
const LOAD_MESSAGES_TIMEOUT = 10_000;
const CREATE_CHAT_TIMEOUT = 10_000;
const DELAY_STARTED_TYPING_INTERVAL = 2_000;
//const PARAM_LIMIT_CHATS = 999;
const PARAM_LIMIT_MESSAGES = 50;
const SEND_DRAFT_DELAY = 3_000;
class UpdateChatOnDraftChangedPatch {
    constructor(onDraftChangedCallback) {
        this._lastDrafts = undefined;
        this._onDraftChangedCallback = onDraftChangedCallback;
    }
    updateDrafts(chats) {
        if (this._lastDrafts === undefined) {
            this._lastDrafts = chats.map((chat) => {
                return { chatId: chat.id, draft: chat.draft_message.content };
            });
            return;
        }
        const filteredChats = chats.filter(chat => {
            const matchingDraft = this._lastDrafts?.find(draft => draft.chatId === chat.id);
            return matchingDraft !== undefined && matchingDraft.draft !== chat.draft_message.content;
        });
        filteredChats.forEach(chat => this._onDraftChangedCallback({
            event: "DRAFT",
            chatData: chat,
            isTyping: null,
        }));
        this._lastDrafts = chats.map((chat) => {
            return { chatId: chat.id, draft: chat.draft_message.content };
        });
    }
}
export class MessengerCoreImpl {
    constructor(sdk, messengerSdk) {
        this._isInitialized = false;
        this._inXR = false;
        this._isLoggedIn = false;
        this._xrStateChangedObserver = null;
        this._modelStatuses = [];
        this._typingMap = new Map(); // (key: chatId, value: isTyping)
        this._isMessageLoadingInProgressForChatId = null;
        this._pinnedModelId = null;
        this._selectedModelId = null;
        this._selectedChatId = null;
        this._loadedMessagesData = new Map(); // (key: chatId, value: firstMessageId)
        this._loadedHistoryData = new Map(); // (key: chatId, value: firstMessageId)
        this._sdkListeners = {
            auth: [],
        };
        this._messengerSdkListeners = {
            auth: [],
            chat: [],
        };
        this._loggedInHandler = async () => {
            this._isLoggedIn = true;
            await this._callbacks.initPanelsAsync(null);
            this._notifyViewsOnLoggedChanged(true);
        };
        this._loggedOutHandler = () => {
            this._isLoggedIn = false;
            this._notifyViewsOnLoggedChanged(false);
        };
        this._chatListChangedHandler = () => {
            this._redrawChatList();
            if (this._updateChatOnDraftChangedPatch === undefined) {
                this._updateChatOnDraftChangedPatch = new UpdateChatOnDraftChangedPatch((args) => {
                    this._lobbyView?.outputMessengerChatChangedForChatList(args);
                    this._broadcastView?.outputMessengerChatChangedForChatList(args);
                });
            }
            this._updateChatOnDraftChangedPatch.updateDrafts(this.messengerSdk.chatManager.chats);
        };
        this._chatChangedHandler = (args) => {
            this._lobbyView?.outputMessengerChatChangedForChatList(args);
            this._lobbyView?.outputMessengerChatChangedForMessages(args);
            this._broadcastView?.outputMessengerChatChangedForChatList(args);
            this._broadcastView?.outputMessengerChatChangedForMessages(args);
            if (args.isTyping !== null) {
                const isTyping = args.isTyping;
                this._typingMap.set(args.chatData.id, isTyping);
                this._lobbyView?.outputMessengerModelTypingChanged(args.chatData.id, isTyping);
                this._broadcastView?.outputMessengerModelTypingChanged(args.chatData.id, isTyping);
            }
            this._redrawBroadcastStatuses();
        };
        this._messageListChangedHandler = () => {
            if (this._isMessageLoadingInProgressForChatId !== null)
                return;
            const chatId = this._callbacks.getSelectedChatId();
            const messageListByDate = chatId === null ? null : (this.messengerSdk.chatManager.messagesByDate.get(chatId) ?? null);
            this._lobbyView?.outputMessengerMessages(chatId, messageListByDate);
            this._broadcastView?.outputMessengerMessages(chatId, messageListByDate);
        };
        this._messageChangedHandler = (_args) => {
            if (this._isMessageLoadingInProgressForChatId !== null)
                return;
            const chatId = this._callbacks.getSelectedChatId();
            const messageListByDate = chatId === null ? null : (this.messengerSdk.chatManager.messagesByDate.get(chatId) ?? null);
            this._lobbyView?.outputMessengerMessages(chatId, messageListByDate);
            this._broadcastView?.outputMessengerMessages(chatId, messageListByDate);
        };
        this._isCreatingChat = false;
        this._callbacks = {
            isLoggedIn: () => {
                return this._isLoggedIn;
            },
            isMessageLoadingInProgress: () => {
                return this._isMessageLoadingInProgressForChatId !== null;
            },
            isAuthorized: () => {
                return this.messengerSdk.authManager.isAuthorized;
            },
            getUserClientId: () => {
                return this.sdk.authManager.authResponse?.clientId ?? "";
            },
            getUnreadMessagesCount: () => {
                const chatArray = this.messengerSdk.chatManager.chats;
                return chatArray.reduce((sum, chat) => sum + chat.unread_count, 0);
            },
            getChatList: () => {
                return this.messengerSdk.chatManager.chats;
            },
            getModelStatuses: () => {
                return this._modelStatuses;
            },
            getIsModelTyping: (chatId) => {
                return this._typingMap.get(chatId) === true;
            },
            getPinnedModelId: () => {
                return this._pinnedModelId;
            },
            setPinnedModelId: (modelId) => {
                this._pinnedModelId = modelId;
            },
            getSelectedModelId: () => {
                return this._selectedModelId;
            },
            setSelectedModelId: (modelId) => {
                // console.log(`%c++++ [1.1] setSelectedModelId() =>\n%c\tmodelId: ${modelId}\n%c\tselectedModelId: ${this._selectedModelId}\n%c\tpinnedModelId: ${this._pinnedModelId}`,
                //     "background-color: silver", "background-color: gold", "background-color: orange", "background-color: gold");
                const chatArray = this.messengerSdk.chatManager.chats;
                if (chatArray.length > 0) {
                    const takeFirstModelIdInList = () => chatArray[0].participant.user_id;
                    if (modelId === null) {
                        modelId = takeFirstModelIdInList();
                    }
                    else if (this._pinnedModelId === null) { // (for the lobby after pinned model in the room and there's no chat with this model)
                        const modelNotFound = chatArray.find(x => x.participant.user_id === modelId) === undefined;
                        if (modelNotFound) {
                            modelId = takeFirstModelIdInList();
                        }
                    }
                }
                this._selectedModelId = modelId;
                let chat = chatArray.find(x => x.participant.user_id !== null && x.participant.user_id === modelId) ?? null;
                this._selectedChatId = chat?.id ?? null;
                // console.log(`%c++++ [1.2] setSelectedModelId() =>\n%c\tnickname: ${chat?.participant.username}\n%c\tselectedModelId: ${this._selectedModelId}\n%c\tselectedChatId: ${this._selectedChatId}\n%c\tpinnedModelId: ${this._pinnedModelId}`,
                //     "background-color: silver", "background-color: lime", "background-color: greenyellow", "background-color: lime", "background-color: greenyellow");
                this._lobbyView?.outputMessengerSelectedModel(modelId, chat);
                this._broadcastView?.outputMessengerSelectedModel(modelId, chat);
                setTimeout(async () => {
                    const chatId = this._selectedChatId;
                    if (chatId !== null) {
                        await this._callbacks.loadChatDataAsync(chatId);
                    }
                    this._callbacks.outputMessages(chatId);
                });
            },
            initPanelsAsync: async (modelId) => {
                // console.log(`%c++++ [2.1] setSelectedModelId() =>\n%c\tmodelId: ${modelId}\n%c\tselectedModelId: ${this._selectedModelId}\n%c\tpinnedModelId: ${this._pinnedModelId}`,
                //     "background-color: silver", "background-color: lime", "background-color: lightgreen", "background-color: lime");
                await this._reloadChatListAsync();
                await this._reloadBroadcastStatusesAsync();
                const pinnedModelId = this._callbacks.getPinnedModelId();
                if (pinnedModelId !== null && modelId === null) {
                    modelId = pinnedModelId;
                }
                const chatArray = this.messengerSdk.chatManager.chats;
                let chat = chatArray.find(x => modelId !== null && x.participant.user_id === modelId) ?? null;
                if (modelId === null && chat === null && chatArray.length > 0) {
                    chat = chatArray[0];
                    modelId = chat.participant.user_id;
                }
                const chatId = chat?.id ?? null;
                // console.log("++++ [2.2] chatArray:", chatArray);
                // console.log("++++ [2.3] chat:", chat);
                this._callbacks.setSelectedModelId(modelId);
                this._redrawChatList();
                this._redrawBroadcastStatuses();
                this._lobbyView?.outputMessengerSelectedModel(modelId, chat);
                this._broadcastView?.outputMessengerSelectedModel(modelId, chat);
                const messageListByDate = chatId === null ? null : (this.messengerSdk.chatManager.messagesByDate.get(chatId) ?? null);
                this._lobbyView?.outputMessengerMessages(chatId, messageListByDate);
                this._broadcastView?.outputMessengerMessages(chatId, messageListByDate);
            },
            outputMessages: (chatId) => {
                const messageListByDate = chatId === null ? null : (this.messengerSdk.chatManager.messagesByDate.get(chatId) ?? null);
                this._lobbyView?.outputMessengerMessages(chatId, messageListByDate);
                this._broadcastView?.outputMessengerMessages(chatId, messageListByDate);
            },
            getSelectedChatId: () => {
                return this._selectedChatId;
            },
            loadChatDataAsync: async (chatId) => {
                if (this._isMessageLoadingInProgressForChatId === chatId)
                    return [];
                await this._callbacks.waitForLoadingAsync();
                this._isMessageLoadingInProgressForChatId = chatId;
                try {
                    this._loadedMessagesData.set(chatId, undefined);
                    this._loadedHistoryData.set(chatId, undefined);
                    const messages = [];
                    let messagesChunk;
                    let lastMessageId = undefined;
                    do {
                        messagesChunk = await this.messengerSdk.chatManager.getMessagesAsync(chatId, "forward", PARAM_LIMIT_MESSAGES, 0, lastMessageId);
                        lastMessageId = messagesChunk.length > 0 ? messagesChunk[messagesChunk.length - 1].id : undefined;
                        messages.push(...messagesChunk);
                    } while (messagesChunk.length === PARAM_LIMIT_MESSAGES);
                    let firstMessageId = messages.length > 0 ? messages[0].id : undefined;
                    messagesChunk = await this.messengerSdk.chatManager.getMessagesAsync(chatId, "backward", PARAM_LIMIT_MESSAGES, 0, firstMessageId);
                    messages.unshift(...messagesChunk);
                    firstMessageId = messages.length > 0 ? messages[0].id : undefined;
                    this._loadedMessagesData.set(chatId, firstMessageId);
                    return messages;
                }
                finally {
                    this._isMessageLoadingInProgressForChatId = null;
                }
            },
            loadChatHistoryAsync: async (chatId) => {
                const firstMessageId = this._loadedMessagesData.get(chatId);
                if (firstMessageId === undefined)
                    return [];
                const prevLoadedFirstMessageId = this._loadedHistoryData.get(chatId);
                if (prevLoadedFirstMessageId !== undefined && prevLoadedFirstMessageId === firstMessageId)
                    return [];
                if (this._isMessageLoadingInProgressForChatId === chatId)
                    return [];
                await this._callbacks.waitForLoadingAsync();
                this._isMessageLoadingInProgressForChatId = chatId;
                try {
                    const result = await this.messengerSdk.chatManager.getMessagesAsync(chatId, "backward", PARAM_LIMIT_MESSAGES, 0, firstMessageId);
                    this._loadedHistoryData.set(chatId, firstMessageId);
                    if (result.length > 0) {
                        this._loadedMessagesData.set(chatId, result[0].id);
                    }
                    return result;
                }
                finally {
                    this._isMessageLoadingInProgressForChatId = null;
                }
            },
            createChatAsync: async (modelId) => {
                if (!this._callbacks.isAuthorized())
                    return null;
                const chatList = this._callbacks.getChatList();
                if (modelId === null)
                    return null;
                const chat = chatList.find(x => x.participant.user_id === modelId);
                if (chat)
                    return chat;
                try {
                    const chat = await this._createChatAsync(modelId);
                    this._selectedChatId = chat.id;
                    await this._updatePanelsForNewChat(modelId, chat);
                    return chat;
                }
                catch (e) {
                    console.error(e);
                    AppView.getInstance().appController.notificationCenter?.showMessage(MessageType.error, text.common_messenger_error_message_not_sent);
                    return null;
                }
            },
            sendMessageAsync: async (chat, message) => {
                try {
                    return chat.participant.reverse_mute_data.is_muted
                        ? null
                        : await this.messengerSdk.chatManager.sendMessageAsync(chat.id, message);
                }
                catch (e) {
                    console.error(e);
                    AppView.getInstance().appController.notificationCenter?.showMessage(MessageType.error, text.common_messenger_error_message_not_sent);
                    return null;
                }
            },
            sendTipsAsync: async (modelId, amount) => {
                try {
                    await this.sdk.chatManager.sendMessengerTipsAsync(modelId, amount);
                }
                catch (e) {
                    if (e.code === 406) {
                        throw new ResponseError(406, e.message);
                    }
                    else {
                        console.error(e);
                        AppView.getInstance().appController.notificationCenter?.showMessage(MessageType.error, text.common_messenger_error_message_not_sent);
                    }
                }
            },
            tryToSendTyping: (chat) => {
                if (!chat.participant.reverse_mute_data.is_muted) {
                    this._throttledSendTypingStarted(chat.id);
                }
            },
            sendDraftMessage: (chat, draftText) => {
                if (!chat.participant.reverse_mute_data.is_muted) {
                    this._throttledSendDraft(chat.id, draftText);
                }
            },
            readMessageAsync: async (chatId, messageId) => {
                try {
                    await this.messengerSdk.chatManager.readMessageAsync(chatId, messageId);
                }
                catch (e) {
                    console.error(e);
                }
            },
            hasDataAsync: async () => {
                await this._callbacks.waitForLoadingAsync();
                return this.messengerSdk.chatManager.chats.length > 0;
            },
            waitForLoadingAsync: async () => {
                await waitFor(() => this._isMessageLoadingInProgressForChatId === null, LOAD_MESSAGES_TIMEOUT, { logErrorOnTimeout: true, rejectOnTimeout: false });
            },
            setModelIsFavoriteAsync: async (modelId, isFavorite) => {
                if (isFavorite) {
                    await this.sdk.clientProfileManager.favorites.addModelAsync(modelId);
                }
                else {
                    await this.sdk.clientProfileManager.favorites.removeModelAsync(modelId);
                }
                const chat = this.messengerSdk.chatManager.chats.find(x => x.participant.user_id === modelId);
                if (chat !== undefined) {
                    chat.participant.is_favorite = isFavorite;
                }
                this._lobbyView?.outputIsFavorite(modelId, isFavorite);
                this._broadcastView?.outputIsFavorite(modelId, isFavorite);
            },
        };
        this._throttledSendTypingStarted = throttle((chatId) => {
            (async () => {
                try {
                    await this.messengerSdk.chatManager.sendStartedTypingAsync(chatId);
                }
                catch (e) {
                    console.error(e);
                }
            })();
        }, DELAY_STARTED_TYPING_INTERVAL);
        this._throttledSendDraft = throttle((chatId, draftText) => {
            (async () => {
                try {
                    await this.messengerSdk.chatManager.updateDraftMessageAsync(chatId, draftText);
                }
                catch (e) {
                    console.error(e);
                }
            })();
        }, SEND_DRAFT_DELAY);
        this.sdk = sdk;
        this.messengerSdk = messengerSdk;
    }
    async initAsync(lobbyView, broadcastView) {
        if (this._isInitialized)
            return;
        this._lobbyView = lobbyView;
        this._broadcastView = broadcastView;
        this._registerListeners();
        this._lobbyView?.setMessengerDataCallbacks(this._callbacks);
        this._broadcastView?.setMessengerDataCallbacks(this._callbacks);
        this._isInitialized = true;
        this._lobbyView?.outputMessengerChats([]);
        this._broadcastView?.outputMessengerChats([]);
        if (this.messengerSdk.authManager.isAuthorized) {
            await this._callbacks.initPanelsAsync(null);
        }
        setInterval(async () => {
            if (this._isInitialized && (this._inXR || AppView.getInstance().isEmulation)) {
                await this._reloadBroadcastStatusesAsync();
                this._redrawBroadcastStatuses();
            }
        }, MessengerCoreImpl._RELOAD_STATUSES_INTERVAL_MILLIS);
    }
    overwriteIsFavorite(modelId, isFavorite) {
        const chat = this.messengerSdk.chatManager.chats.find(x => x.participant.user_id === modelId);
        if (chat)
            chat.participant.is_favorite = isFavorite;
        if (modelId === this._pinnedModelId) {
            this._broadcastView?.overwriteIsFavorite(modelId, isFavorite);
        }
    }
    dispose() {
        this._isInitialized = false;
        this._unregisterListeners();
    }
    _registerListeners() {
        const appView = AppView.getInstance();
        if (appView.xrHelper) {
            this._xrStateChangedObserver = appView.xrHelper.baseExperience.onStateChangedObservable.add((state) => {
                this._inXR = state === WebXRState.IN_XR;
            });
        }
        this._sdkListeners.auth.push(this.sdk.authManager.registerListener("loggedIn", async () => {
            const bssAuthResponse = this.sdk.authManager.authResponse;
            if (bssAuthResponse) {
                const bssSessionId = bssAuthResponse.clientSessionToken;
                const bssClientId = bssAuthResponse.clientId;
                try {
                    await this.messengerSdk.authManager.loginAsync(bssSessionId, EUserType.USER, bssClientId);
                }
                catch (e) {
                    console.error(e);
                    AppView.getInstance().appController.notificationCenter?.showMessage(MessageType.error, text.common_messenger_error_authorized_failed);
                }
            }
        }), this.sdk.authManager.registerListener("loggedOut", async () => {
            await this.messengerSdk.authManager.logoutAsync();
        }));
        this._messengerSdkListeners.auth.push(this.messengerSdk.authManager.registerListener("loggedIn", this._loggedInHandler), this.messengerSdk.authManager.registerListener("loggedOut", this._loggedOutHandler));
        this._messengerSdkListeners.chat.push(this.messengerSdk.chatManager.registerListener("onChatListChanged", this._chatListChangedHandler), this.messengerSdk.chatManager.registerListener("onChatChanged", this._chatChangedHandler), 
        // this.messengerSdk.chatManager.registerListener("onTypingStarted", this._typingStartedHandler),
        // this.messengerSdk.chatManager.registerListener("onTypingFinished", this._typingFinishedHandler),
        this.messengerSdk.chatManager.registerListener("onMessageListByDateChanged", this._messageListChangedHandler), this.messengerSdk.chatManager.registerListener("onMessageStateChanged", this._messageChangedHandler));
    }
    _unregisterListeners() {
        const appView = AppView.getInstance();
        if (appView.xrHelper && this._xrStateChangedObserver !== null) {
            appView.xrHelper.baseExperience.onStateChangedObservable.remove(this._xrStateChangedObserver);
        }
        this.messengerSdk.authManager.unregisterListeners(this._messengerSdkListeners.auth);
        this.messengerSdk.chatManager.unregisterListeners(this._messengerSdkListeners.chat);
        this.sdk.authManager.unregisterListeners(this._sdkListeners.auth);
    }
    async _reloadChatListAsync() {
        await this.messengerSdk.chatManager.getChatsAsync(Env.common.messengerConfig.paramLimitChats);
        await this._callbacks.createChatAsync(this.sdk.broadcastManager.broadcast?.modelId ?? null);
    }
    async _reloadBroadcastStatusesAsync() {
        try {
            const modelIDs = this.messengerSdk.chatManager.chats
                .map(x => x.participant.user_id)
                .filter(x => x !== null);
            this._modelStatuses = await this.sdk.chatManager.loadModelsStatusesAsync(modelIDs);
        }
        catch (e) {
            this._modelStatuses = [];
        }
    }
    _notifyViewsOnLoggedChanged(isAuthorized) {
        this._lobbyView?.outputMessengerLoggedState(isAuthorized);
        this._broadcastView?.outputMessengerLoggedState(isAuthorized);
    }
    _redrawChatList() {
        const chatArray = this.messengerSdk.chatManager.chats;
        const unreadChats = chatArray.filter(x => x.unread_count > 0);
        this._lobbyView?.outputMessengerUnreadMessagesCount(unreadChats);
        this._broadcastView?.outputMessengerUnreadMessagesCount(unreadChats);
        this._lobbyView?.outputMessengerChats(chatArray);
        this._broadcastView?.outputMessengerChats(chatArray);
    }
    _redrawBroadcastStatuses() {
        this._lobbyView?.outputMessengerModelStatuses(this._modelStatuses);
        this._broadcastView?.outputMessengerModelStatuses(this._modelStatuses);
    }
    async _createChatAsync(modelId) {
        await waitFor(() => !this._isCreatingChat, CREATE_CHAT_TIMEOUT, { logErrorOnTimeout: true, rejectOnTimeout: false });
        this._isCreatingChat = true;
        try {
            const chatClientId = await this.sdk.chatManager.getModelChatClientIdAsync(modelId);
            return await this.messengerSdk.chatManager.createPrivateChatAsync(chatClientId);
        }
        finally {
            this._isCreatingChat = false;
        }
    }
    ;
    async _updatePanelsForNewChat(modelId, chat) {
        await this._reloadBroadcastStatusesAsync();
        this._redrawChatList();
        this._redrawBroadcastStatuses();
        this._lobbyView?.outputMessengerSelectedModel(modelId, chat);
        this._broadcastView?.outputMessengerSelectedModel(modelId, chat);
        await this.messengerSdk.chatManager.getMessagesAsync(chat.id, "forward", PARAM_LIMIT_MESSAGES, 0);
        const messageListByDate = this.messengerSdk.chatManager.messagesByDate.get(chat.id) ?? null;
        this._lobbyView?.outputMessengerMessages(chat.id, messageListByDate);
        this._broadcastView?.outputMessengerMessages(chat.id, messageListByDate);
        this._broadcastView?.setMessengerInputControlsAvailability({
            isMuted: chat.participant.reverse_mute_data.is_muted,
            isBanned: chat.participant.block_data.is_blocked,
        });
    }
}
MessengerCoreImpl._RELOAD_STATUSES_INTERVAL_MILLIS = Env.lobby.gallery.reloadIntervalMs;
